/**
 * Hooks for fetching and managing a single CRM object.
 *
 * Two surfaces are exported:
 *
 * - `useCRMObject(orgId, entityType, objectId, userId)` — fetch + mutations.
 *   Used by full-page record views (CRMObjectDetailPage, MeetingSummaryFullPage)
 *   that don't have the object via prop and need both data and mutations.
 *
 * - `useCrmObjectMutations(orgId, entityType, object, userId)` — mutations only,
 *   no fetch. Used by callers that already have the `object` via prop (e.g.
 *   the slideout's RecordActionMenu, which is rendered inside `{object && …}`).
 *   Avoids a second redundant fetch of the same entity that the slideout
 *   already loaded.
 */

import { useCallback, useEffect, useRef } from 'react';

import type { SubmitEventsResponse } from '@/types/crm';

import { createEventBuilder } from '../../core/EventBuilder';
import { createEventBundler, type EventBundler } from '../../core/EventBundler';
import type { CrmObject, EntityTypeName } from '../../core/types';
import { useSubmitEventsOptimistic } from '../../query/mutations';
import { useCrmObject } from '../../store/hooks';

/**
 * Options for useCRMObject hook.
 */
export interface UseCRMObjectOptions {
  /** Whether the query is enabled */
  enabled?: boolean;
}

/**
 * Result from useCRMObject hook.
 */
export interface UseCRMObjectResult {
  /** The CRM object */
  object: CrmObject | undefined;

  /** Loading state */
  loading: boolean;

  /** Error message if failed */
  error: string | null;

  /**
   * HTTP status code of the fetch error, when available. Lets record views
   * discriminate authoritative failures (403, post-pending 404) from a 404
   * for a pending optimistic creation the server hasn't projected yet — see
   * `usePendingCreation404Recovery`.
   */
  errorStatusCode: number | undefined;

  /** Refetch the entity query (stable per React Query). */
  refetch: () => Promise<unknown>;

  /** Update a property on the object */
  update: (field: string, value: unknown) => Promise<void>;

  /** Delete the object. Resolves to the raw submit-events response so callers
   *  can discriminate a sync delete from a 202 async job dispatch. */
  delete: () => Promise<SubmitEventsResponse>;
}

/**
 * Result from useCrmObjectMutations hook.
 */
export interface UseCrmObjectMutationsResult {
  /** Update a property on the object */
  update: (field: string, value: unknown) => Promise<void>;

  /** Delete the object. Resolves to the raw submit-events response so callers
   *  can discriminate a sync delete from a 202 async job dispatch. */
  delete: () => Promise<SubmitEventsResponse>;
}

/**
 * Mutation-only hook for a CRM object the caller already has via prop.
 *
 * Returns `update` and `delete` callbacks that operate on the supplied
 * `object`. Performs no fetch — callers that need to load the object should
 * use `useCRMObject` (full-page views) instead.
 *
 * Why this exists: the slideout's `RecordActionMenu` is rendered inside the
 * `{object && (...)}` gate of `SingleSlideout`, which means it always has a
 * fully-loaded object via prop. Previously it called `useCRMObject` only for
 * the `delete` callback, but that hook internally fired a second `useQuery`
 * with the heavy `includeAssociatedObjects: true, includeAssociations: true`
 * shape — duplicating the slideout's own fetch and racing the slideout's
 * `upsertObject` ~1 second later. Splitting the mutations off lets the
 * slideout-context callers skip that fetch entirely.
 */
export function useCrmObjectMutations(
  orgId: number | undefined,
  entityType: EntityTypeName,
  object: CrmObject | undefined,
  userId: number | undefined
): UseCrmObjectMutationsResult {
  // Mutation for updates
  const { mutateAsync: submitEvents } = useSubmitEventsOptimistic(
    orgId ?? 0,
    entityType
  );

  // Event bundler for auto-bundling rapid property updates
  const bundlerRef = useRef<EventBundler | null>(null);

  // Initialize bundler and cleanup on unmount
  useEffect(() => {
    if (!bundlerRef.current) {
      bundlerRef.current = createEventBundler();
    }
    return () => {
      bundlerRef.current?.clear();
      bundlerRef.current = null;
    };
  }, []);

  /**
   * Update a property on the object.
   *
   * Uses EventBundler to automatically bundle rapid property updates
   * within a 100ms window into a single entity event.
   *
   * useCallback gives consumers a stable function reference so passing
   * `update` to memoized children or including it in effect deps doesn't
   * tear down everything on every parent rerender.
   *
   * @param field - Property name (with or without 'ahoy_' prefix)
   * @param value - New value
   */
  const update = useCallback(
    async (field: string, value: unknown): Promise<void> => {
      if (!object || !orgId || !bundlerRef.current || !userId) {
        return;
      }

      // Add 'ahoy_' prefix if not present
      const propertyName = field.startsWith('ahoy_') ? field : `ahoy_${field}`;

      // Add to bundler - will auto-flush after 100ms or when max properties reached
      bundlerRef.current.addPropertyUpdate(
        object.entity_id,
        entityType,
        propertyName,
        value,
        async (properties: Record<string, unknown>) => {
          // This callback is called when bundle is ready to flush
          if (!object || !orgId) {
            return;
          }

          const builder = createEventBuilder({
            sourceType: 'ahoy_user',
            sourceId: String(userId),
            startSeq: object.last_event_seq,
          });

          // Use propertiesUpdated() to create a bundled event
          builder.propertiesUpdated(object.entity_id, entityType, properties);

          const events = builder.build();
          await submitEvents({ events });
        }
      );
    },
    [object, orgId, userId, entityType, submitEvents]
  );

  /**
   * Delete the object.
   * @throws Error if object data is unavailable
   */
  const deleteObject = useCallback(async (): Promise<SubmitEventsResponse> => {
    if (!object || !orgId || !userId) {
      throw new Error('Cannot delete: object data unavailable');
    }

    const builder = createEventBuilder({
      sourceType: 'ahoy_user',
      sourceId: String(userId),
      startSeq: object.last_event_seq,
    });

    builder.objectDeleted(object.entity_id, object.entity_type_name);

    return await submitEvents({ events: builder.build() });
  }, [object, orgId, userId, submitEvents]);

  return {
    update,
    delete: deleteObject,
  };
}

/**
 * Generic hook to fetch and manage a CRM object.
 *
 * @param orgId - Organization ID
 * @param entityType - CRM entity type (e.g., "ahoy_contact")
 * @param objectId - Object entity ID (UUID)
 * @param userId - User ID performing the action
 * @param options - Hook options
 * @returns Object, loading state, error, and mutation functions
 *
 * @example
 * ```typescript
 * const { object, loading, update, delete: deleteObject } = useCRMObject(
 *   orgId,
 *   'ahoy_contact',
 *   contactId,
 *   userId
 * );
 *
 * // Update a property
 * await update('ahoy_email', 'new@email.com');
 *
 * // Delete the object
 * await delete();
 * ```
 */
export function useCRMObject(
  orgId: number | undefined,
  entityType: EntityTypeName,
  objectId: string | undefined,
  userId: number | undefined,
  options: UseCRMObjectOptions = {}
): UseCRMObjectResult {
  const { enabled = true } = options;

  // Fetch object details via store-aware hook (real-time WS updates apply immediately).
  //
  // Opt in to `associations` but NOT `associated_objects`. Full-page record
  // views (CRMObjectDetailPage, MeetingSummaryFullPage) need `.associations`
  // for overview edge-filtering and AssociationsTab grouping/counts. They do
  // NOT need the 416KB `associated_objects` map — since PR #12956 the overview
  // filters edges before hydrating, and AssociationsTab hydrates badges via the
  // by-entity-ids batcher. The two full-page readers that previously leaned on
  // the map (EmailThreadOverviewEnrichment source-row resolution and
  // research-brief compose-email resolution) now hydrate their targets via
  // `useCrmObjectLive` / `useAssociations` instead. Dropping the map cuts the
  // measured response from ~503KB to ~87KB.
  //
  // `associated_objects` is a legacy opt-in still available on the backend but
  // no longer used by this hook. The backend gates the block on the query param
  // (apps/crm_events/viewsets/object_viewsets.py) so omitting it is a safe
  // no-op: the server simply ships no map.
  //
  // Lean callers (settings pages reading two properties) should call the
  // lower-level `useCrmObject` directly and leave both options unset.
  // Slideout-context callers that already have `object` via prop should use
  // `useCrmObjectMutations` instead — see the doc on that hook for why.
  const { object, isLoading, error, refetch } = useCrmObject(
    orgId ?? 0,
    entityType,
    objectId,
    {
      enabled: Boolean(orgId && objectId && enabled),
      includeAssociatedObjects: false,
      includeAssociations: true,
    }
  );

  const { update, delete: deleteObject } = useCrmObjectMutations(
    orgId,
    entityType,
    object,
    userId
  );

  return {
    object,
    loading: isLoading,
    error: error?.message ?? null,
    errorStatusCode: (error as { statusCode?: number } | null)?.statusCode,
    refetch,
    update,
    delete: deleteObject,
  };
}
